import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ImageIcon,
  UploadIcon,
  SparklesIcon,
  LayoutTemplateIcon,
  CodeIcon,
  AlertCircleIcon,
  MessageSquareIcon,
  BrainIcon,
  RocketIcon,
  SendIcon,
  ChevronDownIcon,
  ZapIcon,
  HeartIcon,
  StarIcon,
  TrendingUpIcon,
  ShoppingCartIcon,
  CameraIcon,
  MusicIcon,
  VideoIcon,
  FileTextIcon,
  BookOpenIcon,
  GlobeIcon,
  MailIcon,
  PhoneIcon,
  MapPinIcon,
  CalendarIcon,
  ClockIcon,
  SearchIcon,
  SettingsIcon,
  UserIcon,
  UsersIcon,
  BoxIcon,
  PackageIcon,
  TruckIcon,
  CreditCardIcon,
  DollarSignIcon,
  PieChartIcon,
  BarChartIcon,
  TrendingDownIcon,
  ActivityIcon,
  XIcon,
  PlusIcon,
  Trash2Icon,
  EyeIcon,
  SlidersIcon,
  CheckSquareIcon,
  CircleIcon,
  ToggleLeftIcon,
} from 'lucide-react'
import { InfoTooltip } from '../components/InfoTooltip'
type AppType = 'prompt' | 'template' | 'custom'
type Template = {
  id: string
  title: string
  description: string
  icon: React.ReactNode
}
type IconOption = {
  id: string
  icon: React.ReactNode
  name: string
}
type ColorOption = {
  id: string
  name: string
  gradient: string
}
type PreferenceFieldType =
  | 'checkbox-group'
  | 'radio-group'
  | 'slider'
  | 'text-input'
  | 'dropdown'
  | 'toggle'
type PreferenceField = {
  id: string
  type: PreferenceFieldType
  label: string
  options?: string[]
  min?: number
  max?: number
  defaultValue?: any
}
type PreferenceSection = {
  id: string
  title: string
  fields: PreferenceField[]
}
type FormStyling = {
  layout: 'single' | 'two-column'
  buttonColor: string
  buttonStyle: 'rounded' | 'square' | 'pill'
  spacing: 'compact' | 'normal' | 'relaxed'
  borderRadius: 'none' | 'small' | 'medium' | 'large'
  colorScheme: 'indigo' | 'blue' | 'purple' | 'green' | 'orange' | 'gray'
}
export const Create = () => {
  const navigate = useNavigate()
  const [appType, setAppType] = useState<AppType>('prompt')
  const [selectedTemplate, setSelectedTemplate] = useState<string>('')
  const [iconColor, setIconColor] = useState(
    'bg-gradient-to-br from-indigo-500 to-purple-600',
  )
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [selectedIcon, setSelectedIcon] = useState('sparkles')
  const [showIconSelect, setShowIconSelect] = useState(false)
  const [showColorPicker, setShowColorPicker] = useState(false)
  // Preference Builder State
  const [preferenceSections, setPreferenceSections] = useState<
    PreferenceSection[]
  >([
    {
      id: '1',
      title: 'User Preferences',
      fields: [
        {
          id: '1',
          type: 'checkbox-group',
          label: 'Select Options',
          options: ['Option 1', 'Option 2', 'Option 3'],
        },
      ],
    },
  ])
  const [editingField, setEditingField] = useState<string | null>(null)
  // Form Styling State
  const [formStyling, setFormStyling] = useState<FormStyling>({
    layout: 'single',
    buttonColor: 'indigo',
    buttonStyle: 'rounded',
    spacing: 'normal',
    borderRadius: 'medium',
    colorScheme: 'indigo',
  })
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    description: '',
    aiModel: '',
    visibility: 'private',
    allowImageUpload: false,
    allowFileUpload: false,
    fileTypes: [] as string[],
    enableVoiceInput: false,
    maxFileSize: '5',
    enableCodeExecution: false,
  })
  const fileTypeOptions = [
    {
      value: 'pdf',
      label: 'PDF Documents',
    },
    {
      value: 'doc',
      label: 'Word Documents',
    },
    {
      value: 'txt',
      label: 'Text Files',
    },
    {
      value: 'csv',
      label: 'CSV Files',
    },
    {
      value: 'json',
      label: 'JSON Files',
    },
    {
      value: 'xml',
      label: 'XML Files',
    },
  ]
  const fileSizeOptions = [
    {
      value: '1',
      label: '1 MB',
    },
    {
      value: '2',
      label: '2 MB',
    },
    {
      value: '5',
      label: '5 MB',
    },
    {
      value: '10',
      label: '10 MB',
    },
  ]
  const toggleFileType = (fileType: string) => {
    setFormData({
      ...formData,
      fileTypes: formData.fileTypes.includes(fileType)
        ? formData.fileTypes.filter((t) => t !== fileType)
        : [...formData.fileTypes, fileType],
    })
  }
  const handleCreateApp = () => {
    // Navigate to App Store Listing page with form data
    navigate('/app-store-listing', {
      state: {
        title: formData.title,
        slug: formData.slug,
        description: formData.description,
        icon: selectedIcon,
        iconColor: iconColor,
        categories: selectedCategories,
        aiModel: formData.aiModel,
        visibility: formData.visibility,
        allowImageUpload: formData.allowImageUpload,
        allowFileUpload: formData.allowFileUpload,
        fileTypes: formData.fileTypes,
        enableVoiceInput: formData.enableVoiceInput,
        maxFileSize: formData.maxFileSize,
        enableCodeExecution: formData.enableCodeExecution,
        preferenceSections:
          selectedTemplate === 'preference-based' ? preferenceSections : null,
        formStyling:
          selectedTemplate === 'preference-based' ? formStyling : null,
      },
    })
  }
  const templates: Template[] = [
    {
      id: 'simple-prompt',
      title: 'Simple Prompt',
      description: 'Basic prompt-based interaction',
      icon: <SparklesIcon className="h-6 w-6" />,
    },
    {
      id: 'preference-based',
      title: 'App with User Preferences',
      description: 'Create an app with customizable user preference forms',
      icon: <SlidersIcon className="h-6 w-6" />,
    },
    {
      id: 'image-text',
      title: 'Image + Text',
      description: 'Generate and process images with text',
      icon: <ImageIcon className="h-6 w-6" />,
    },
    {
      id: 'chat-bot',
      title: 'Chat Bot',
      description: 'Interactive conversational interface',
      icon: <MessageSquareIcon className="h-6 w-6" />,
    },
    {
      id: 'assistant',
      title: 'AI Assistant',
      description: 'Task-focused AI helper',
      icon: <BrainIcon className="h-6 w-6" />,
    },
  ]
  const categories = [
    'Productivity',
    'Writing & Content',
    'Image Generation',
    'Code & Development',
    'Audio & Music',
    'Video Processing',
    'Data Analysis',
    'Education',
    'Research',
    'Entertainment',
  ]
  const icons: IconOption[] = [
    {
      id: 'sparkles',
      icon: <SparklesIcon className="h-6 w-6" />,
      name: 'Sparkles',
    },
    {
      id: 'rocket',
      icon: <RocketIcon className="h-6 w-6" />,
      name: 'Rocket',
    },
    {
      id: 'brain',
      icon: <BrainIcon className="h-6 w-6" />,
      name: 'Brain',
    },
    {
      id: 'message',
      icon: <MessageSquareIcon className="h-6 w-6" />,
      name: 'Message',
    },
    {
      id: 'zap',
      icon: <ZapIcon className="h-6 w-6" />,
      name: 'Zap',
    },
    {
      id: 'heart',
      icon: <HeartIcon className="h-6 w-6" />,
      name: 'Heart',
    },
    {
      id: 'star',
      icon: <StarIcon className="h-6 w-6" />,
      name: 'Star',
    },
    {
      id: 'trending',
      icon: <TrendingUpIcon className="h-6 w-6" />,
      name: 'Trending',
    },
    {
      id: 'cart',
      icon: <ShoppingCartIcon className="h-6 w-6" />,
      name: 'Cart',
    },
    {
      id: 'camera',
      icon: <CameraIcon className="h-6 w-6" />,
      name: 'Camera',
    },
    {
      id: 'music',
      icon: <MusicIcon className="h-6 w-6" />,
      name: 'Music',
    },
    {
      id: 'video',
      icon: <VideoIcon className="h-6 w-6" />,
      name: 'Video',
    },
    {
      id: 'file',
      icon: <FileTextIcon className="h-6 w-6" />,
      name: 'File',
    },
    {
      id: 'book',
      icon: <BookOpenIcon className="h-6 w-6" />,
      name: 'Book',
    },
    {
      id: 'globe',
      icon: <GlobeIcon className="h-6 w-6" />,
      name: 'Globe',
    },
    {
      id: 'mail',
      icon: <MailIcon className="h-6 w-6" />,
      name: 'Mail',
    },
    {
      id: 'phone',
      icon: <PhoneIcon className="h-6 w-6" />,
      name: 'Phone',
    },
    {
      id: 'map',
      icon: <MapPinIcon className="h-6 w-6" />,
      name: 'Map',
    },
    {
      id: 'calendar',
      icon: <CalendarIcon className="h-6 w-6" />,
      name: 'Calendar',
    },
    {
      id: 'clock',
      icon: <ClockIcon className="h-6 w-6" />,
      name: 'Clock',
    },
    {
      id: 'search',
      icon: <SearchIcon className="h-6 w-6" />,
      name: 'Search',
    },
    {
      id: 'settings',
      icon: <SettingsIcon className="h-6 w-6" />,
      name: 'Settings',
    },
    {
      id: 'user',
      icon: <UserIcon className="h-6 w-6" />,
      name: 'User',
    },
    {
      id: 'users',
      icon: <UsersIcon className="h-6 w-6" />,
      name: 'Users',
    },
    {
      id: 'box',
      icon: <BoxIcon className="h-6 w-6" />,
      name: 'Box',
    },
    {
      id: 'package',
      icon: <PackageIcon className="h-6 w-6" />,
      name: 'Package',
    },
    {
      id: 'truck',
      icon: <TruckIcon className="h-6 w-6" />,
      name: 'Truck',
    },
    {
      id: 'card',
      icon: <CreditCardIcon className="h-6 w-6" />,
      name: 'Card',
    },
    {
      id: 'dollar',
      icon: <DollarSignIcon className="h-6 w-6" />,
      name: 'Dollar',
    },
    {
      id: 'pie',
      icon: <PieChartIcon className="h-6 w-6" />,
      name: 'Pie Chart',
    },
    {
      id: 'bar',
      icon: <BarChartIcon className="h-6 w-6" />,
      name: 'Bar Chart',
    },
    {
      id: 'activity',
      icon: <ActivityIcon className="h-6 w-6" />,
      name: 'Activity',
    },
    {
      id: 'image',
      icon: <ImageIcon className="h-6 w-6" />,
      name: 'Image',
    },
  ]
  const colorOptions: ColorOption[] = [
    {
      id: 'indigo-purple',
      name: 'Indigo Purple',
      gradient: 'bg-gradient-to-br from-indigo-500 to-purple-600',
    },
    {
      id: 'blue-cyan',
      name: 'Blue Cyan',
      gradient: 'bg-gradient-to-br from-blue-500 to-cyan-400',
    },
    {
      id: 'purple-pink',
      name: 'Purple Pink',
      gradient: 'bg-gradient-to-br from-purple-500 to-pink-500',
    },
    {
      id: 'orange-red',
      name: 'Orange Red',
      gradient: 'bg-gradient-to-br from-orange-500 to-red-500',
    },
    {
      id: 'green-teal',
      name: 'Green Teal',
      gradient: 'bg-gradient-to-br from-green-500 to-teal-500',
    },
    {
      id: 'yellow-orange',
      name: 'Yellow Orange',
      gradient: 'bg-gradient-to-br from-yellow-400 to-orange-500',
    },
    {
      id: 'pink-rose',
      name: 'Pink Rose',
      gradient: 'bg-gradient-to-br from-pink-400 to-rose-500',
    },
    {
      id: 'violet-purple',
      name: 'Violet Purple',
      gradient: 'bg-gradient-to-br from-violet-500 to-purple-600',
    },
    {
      id: 'emerald-green',
      name: 'Emerald Green',
      gradient: 'bg-gradient-to-br from-emerald-500 to-green-600',
    },
    {
      id: 'sky-blue',
      name: 'Sky Blue',
      gradient: 'bg-gradient-to-br from-sky-400 to-blue-500',
    },
    {
      id: 'amber-orange',
      name: 'Amber Orange',
      gradient: 'bg-gradient-to-br from-amber-400 to-orange-600',
    },
    {
      id: 'slate-gray',
      name: 'Slate Gray',
      gradient: 'bg-gradient-to-br from-slate-600 to-gray-700',
    },
  ]
  const aiModels = [
    {
      value: 'gpt-4',
      label: 'GPT-4',
    },
    {
      value: 'gpt-3.5',
      label: 'GPT-3.5',
    },
    {
      value: 'claude',
      label: 'Claude 2',
    },
    {
      value: 'gemini',
      label: 'Gemini Pro',
    },
    {
      value: 'mistral',
      label: 'Mistral',
    },
    {
      value: 'llama',
      label: 'Llama 2',
    },
  ]
  // Preference Builder Functions
  const addSection = () => {
    const newSection: PreferenceSection = {
      id: Date.now().toString(),
      title: 'New Section',
      fields: [],
    }
    setPreferenceSections([...preferenceSections, newSection])
  }
  const removeSection = (sectionId: string) => {
    setPreferenceSections(preferenceSections.filter((s) => s.id !== sectionId))
  }
  const updateSection = (sectionId: string, title: string) => {
    setPreferenceSections(
      preferenceSections.map((s) => (s.id === sectionId ? { ...s, title } : s)),
    )
  }
  const addField = (sectionId: string, type: PreferenceFieldType) => {
    const newField: PreferenceField = {
      id: Date.now().toString(),
      type,
      label: 'New Field',
      options:
        type === 'checkbox-group' ||
        type === 'radio-group' ||
        type === 'dropdown'
          ? ['Option 1', 'Option 2']
          : undefined,
      min: type === 'slider' ? 0 : undefined,
      max: type === 'slider' ? 10 : undefined,
    }
    setPreferenceSections(
      preferenceSections.map((s) =>
        s.id === sectionId
          ? {
              ...s,
              fields: [...s.fields, newField],
            }
          : s,
      ),
    )
  }
  const removeField = (sectionId: string, fieldId: string) => {
    setPreferenceSections(
      preferenceSections.map((s) =>
        s.id === sectionId
          ? {
              ...s,
              fields: s.fields.filter((f) => f.id !== fieldId),
            }
          : s,
      ),
    )
  }
  const updateField = (
    sectionId: string,
    fieldId: string,
    updates: Partial<PreferenceField>,
  ) => {
    setPreferenceSections(
      preferenceSections.map((s) =>
        s.id === sectionId
          ? {
              ...s,
              fields: s.fields.map((f) =>
                f.id === fieldId
                  ? {
                      ...f,
                      ...updates,
                    }
                  : f,
              ),
            }
          : s,
      ),
    )
  }
  const addOption = (sectionId: string, fieldId: string) => {
    setPreferenceSections(
      preferenceSections.map((s) =>
        s.id === sectionId
          ? {
              ...s,
              fields: s.fields.map((f) =>
                f.id === fieldId
                  ? {
                      ...f,
                      options: [
                        ...(f.options || []),
                        `Option ${(f.options?.length || 0) + 1}`,
                      ],
                    }
                  : f,
              ),
            }
          : s,
      ),
    )
  }
  const updateOption = (
    sectionId: string,
    fieldId: string,
    optionIndex: number,
    value: string,
  ) => {
    setPreferenceSections(
      preferenceSections.map((s) =>
        s.id === sectionId
          ? {
              ...s,
              fields: s.fields.map((f) =>
                f.id === fieldId
                  ? {
                      ...f,
                      options: f.options?.map((opt, idx) =>
                        idx === optionIndex ? value : opt,
                      ),
                    }
                  : f,
              ),
            }
          : s,
      ),
    )
  }
  const removeOption = (
    sectionId: string,
    fieldId: string,
    optionIndex: number,
  ) => {
    setPreferenceSections(
      preferenceSections.map((s) =>
        s.id === sectionId
          ? {
              ...s,
              fields: s.fields.map((f) =>
                f.id === fieldId
                  ? {
                      ...f,
                      options: f.options?.filter(
                        (_, idx) => idx !== optionIndex,
                      ),
                    }
                  : f,
              ),
            }
          : s,
      ),
    )
  }
  const fieldTypeOptions = [
    {
      value: 'checkbox-group',
      label: 'Checkbox Group',
      icon: <CheckSquareIcon className="h-4 w-4" />,
    },
    {
      value: 'radio-group',
      label: 'Radio Group',
      icon: <CircleIcon className="h-4 w-4" />,
    },
    {
      value: 'slider',
      label: 'Slider',
      icon: <SlidersIcon className="h-4 w-4" />,
    },
    {
      value: 'text-input',
      label: 'Text Input',
      icon: <FileTextIcon className="h-4 w-4" />,
    },
    {
      value: 'dropdown',
      label: 'Dropdown',
      icon: <ChevronDownIcon className="h-4 w-4" />,
    },
    {
      value: 'toggle',
      label: 'Toggle',
      icon: <ToggleLeftIcon className="h-4 w-4" />,
    },
  ]
  // Get styling classes based on settings
  const getSpacingClass = () => {
    switch (formStyling.spacing) {
      case 'compact':
        return 'space-y-4'
      case 'relaxed':
        return 'space-y-8'
      default:
        return 'space-y-6'
    }
  }
  const getBorderRadiusClass = () => {
    switch (formStyling.borderRadius) {
      case 'none':
        return 'rounded-none'
      case 'small':
        return 'rounded'
      case 'large':
        return 'rounded-2xl'
      default:
        return 'rounded-lg'
    }
  }
  const getButtonStyleClass = () => {
    const baseColor = `bg-${formStyling.buttonColor}-600 hover:bg-${formStyling.buttonColor}-700`
    switch (formStyling.buttonStyle) {
      case 'square':
        return `${baseColor} rounded-none`
      case 'pill':
        return `${baseColor} rounded-full`
      default:
        return `${baseColor} rounded-lg`
    }
  }
  const getColorSchemeClasses = () => {
    const color = formStyling.colorScheme
    return {
      checkbox: `text-${color}-600`,
      radio: `text-${color}-600`,
      slider: `accent-${color}-600`,
      toggle: `peer-checked:bg-${color}-600`,
      border: `border-${color}-200`,
      focus: `focus:ring-${color}-300`,
    }
  }
  // Preview Component
  const PreferencePreview = () => {
    const colorClasses = getColorSchemeClasses()
    const spacingClass = getSpacingClass()
    const borderClass = getBorderRadiusClass()
    return (
      <div
        className={`bg-white shadow-lg border border-gray-200 p-6 h-full overflow-y-auto ${borderClass}`}
      >
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
          <h3 className="text-xl font-bold text-gray-900">User Preferences</h3>
          <SettingsIcon className="h-6 w-6 text-gray-400" />
        </div>
        {preferenceSections.length === 0 ? (
          <div className="text-center py-12">
            <SlidersIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">
              Add sections and fields to see preview
            </p>
          </div>
        ) : (
          <div className={spacingClass}>
            {preferenceSections.map((section) => (
              <div key={section.id}>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                  {section.title}
                </h4>
                <div className={spacingClass}>
                  {section.fields.map((field) => (
                    <div key={field.id}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {field.label}
                      </label>
                      {field.type === 'checkbox-group' && (
                        <div
                          className={`grid ${formStyling.layout === 'two-column' ? 'grid-cols-2' : 'grid-cols-1'} gap-2`}
                        >
                          {field.options?.map((option, idx) => (
                            <label
                              key={idx}
                              className={`flex items-center space-x-2 p-2 border border-gray-200 hover:bg-gray-50 cursor-pointer ${borderClass}`}
                            >
                              <input
                                type="checkbox"
                                className={`rounded ${colorClasses.checkbox}`}
                              />
                              <span className="text-sm">{option}</span>
                            </label>
                          ))}
                        </div>
                      )}
                      {field.type === 'radio-group' && (
                        <div
                          className={`grid ${formStyling.layout === 'two-column' ? 'grid-cols-2' : 'grid-cols-1'} gap-2`}
                        >
                          {field.options?.map((option, idx) => (
                            <label
                              key={idx}
                              className={`flex items-center space-x-2 p-2 border border-gray-200 hover:bg-gray-50 cursor-pointer ${borderClass}`}
                            >
                              <input
                                type="radio"
                                name={field.id}
                                className={colorClasses.radio}
                              />
                              <span className="text-sm">{option}</span>
                            </label>
                          ))}
                        </div>
                      )}
                      {field.type === 'slider' && (
                        <div>
                          <input
                            type="range"
                            min={field.min}
                            max={field.max}
                            className={`w-full ${colorClasses.slider}`}
                          />
                          <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>{field.min}</span>
                            <span>{field.max}</span>
                          </div>
                        </div>
                      )}
                      {field.type === 'text-input' && (
                        <input
                          type="text"
                          placeholder="Enter text..."
                          className={`w-full border border-gray-200 px-3 py-2 text-sm ${borderClass}`}
                        />
                      )}
                      {field.type === 'dropdown' && (
                        <select
                          className={`w-full border border-gray-200 px-3 py-2 text-sm ${borderClass}`}
                        >
                          <option>Select an option</option>
                          {field.options?.map((option, idx) => (
                            <option key={idx}>{option}</option>
                          ))}
                        </select>
                      )}
                      {field.type === 'toggle' && (
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input type="checkbox" className="sr-only peer" />
                          <div
                            className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 ${colorClasses.focus} rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${colorClasses.toggle}`}
                          ></div>
                          <span className="text-sm text-gray-700">
                            Enable {field.label}
                          </span>
                        </label>
                      )}
                    </div>
                  ))}
                  {section.fields.length === 0 && (
                    <p className="text-sm text-gray-400 italic">
                      No fields added yet
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        {/* Preview Submit Button */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <button
            className={`w-full px-6 py-3 text-white font-medium transition-colors ${getButtonStyleClass()}`}
          >
            Submit Preferences
          </button>
        </div>
      </div>
    )
  }
  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <h1 className="text-2xl font-bold mb-8">Create New App</h1>
      {/* App Type Selection */}
      <div className="bg-white rounded-lg p-6 mb-8 shadow-sm">
        <h2 className="text-lg font-semibold mb-4">Choose App Type</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            className={`p-4 rounded-lg border-2 flex flex-col items-center ${appType === 'prompt' ? 'border-indigo-600 bg-indigo-50' : 'border-gray-200 hover:border-indigo-600 hover:bg-gray-50'}`}
            onClick={() => setAppType('prompt')}
          >
            <SparklesIcon className="h-6 w-6 mb-2 text-indigo-600" />
            <span className="font-medium">Prompt App</span>
            <span className="text-xs text-gray-500 mt-1">
              Simple prompt-based app
            </span>
          </button>
          <button
            className={`p-4 rounded-lg border-2 flex flex-col items-center ${appType === 'template' ? 'border-indigo-600 bg-indigo-50' : 'border-gray-200 hover:border-indigo-600 hover:bg-gray-50'}`}
            onClick={() => setAppType('template')}
          >
            <LayoutTemplateIcon className="h-6 w-6 mb-2 text-indigo-600" />
            <span className="font-medium">Use Template</span>
            <span className="text-xs text-gray-500 mt-1">
              Start with a template
            </span>
          </button>
          <button
            className={`p-4 rounded-lg border-2 flex flex-col items-center ${appType === 'custom' ? 'border-indigo-600 bg-indigo-50' : 'border-gray-200 hover:border-indigo-600 hover:bg-gray-50'}`}
            onClick={() => setAppType('custom')}
          >
            <CodeIcon className="h-6 w-6 mb-2 text-indigo-600" />
            <span className="font-medium">Custom App</span>
            <span className="text-xs text-gray-500 mt-1">
              Advanced customization
            </span>
          </button>
        </div>
      </div>
      {/* Template Selection */}
      {appType === 'template' && (
        <div className="bg-white rounded-lg p-6 mb-8 shadow-sm">
          <h2 className="text-lg font-semibold mb-4">Select Template</h2>
          <div className="grid grid-cols-1 gap-4">
            {templates.map((template) => (
              <button
                key={template.id}
                className={`p-4 rounded-lg border-2 flex items-start ${selectedTemplate === template.id ? 'border-indigo-600 bg-indigo-50' : 'border-gray-200 hover:border-indigo-600 hover:bg-gray-50'}`}
                onClick={() => setSelectedTemplate(template.id)}
              >
                <div className="flex-shrink-0 text-indigo-600 mr-4">
                  {template.icon}
                </div>
                <div className="text-left">
                  <span className="font-medium">{template.title}</span>
                  <p className="text-sm text-gray-500 mt-1">
                    {template.description}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Preference Builder - Only show when preference-based template is selected */}
      {appType === 'template' && selectedTemplate === 'preference-based' && (
        <>
          <div className="bg-white rounded-lg p-6 mb-8 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold flex items-center">
                  <SlidersIcon className="h-5 w-5 mr-2 text-indigo-600" />
                  Design User Preferences
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Build custom preference forms that your users will see
                </p>
              </div>
              <button
                onClick={addSection}
                className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Section
              </button>
            </div>
            {/* Split View */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left: Builder */}
              <div className="space-y-6 max-h-[600px] overflow-y-auto pr-4">
                {preferenceSections.map((section, sectionIdx) => (
                  <div
                    key={section.id}
                    className="border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <input
                        type="text"
                        value={section.title}
                        onChange={(e) =>
                          updateSection(section.id, e.target.value)
                        }
                        className="font-semibold text-gray-900 border-0 border-b border-transparent hover:border-gray-300 focus:border-indigo-500 focus:outline-none px-1"
                      />
                      <button
                        onClick={() => removeSection(section.id)}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <Trash2Icon className="h-4 w-4" />
                      </button>
                    </div>
                    {/* Fields */}
                    <div className="space-y-3 mb-4">
                      {section.fields.map((field) => (
                        <div
                          key={field.id}
                          className="bg-gray-50 rounded-lg p-3 border border-gray-200"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <input
                              type="text"
                              value={field.label}
                              onChange={(e) =>
                                updateField(section.id, field.id, {
                                  label: e.target.value,
                                })
                              }
                              className="text-sm font-medium bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-indigo-500 focus:outline-none px-1"
                            />
                            <button
                              onClick={() => removeField(section.id, field.id)}
                              className="text-red-500 hover:text-red-700 p-1"
                            >
                              <XIcon className="h-4 w-4" />
                            </button>
                          </div>
                          <div className="text-xs text-gray-500 mb-2">
                            Type: {field.type}
                          </div>
                          {/* Options for checkbox/radio/dropdown */}
                          {(field.type === 'checkbox-group' ||
                            field.type === 'radio-group' ||
                            field.type === 'dropdown') && (
                            <div className="space-y-2">
                              <div className="text-xs font-medium text-gray-600 mb-1">
                                Options:
                              </div>
                              {field.options?.map((option, optIdx) => (
                                <div
                                  key={optIdx}
                                  className="flex items-center space-x-2"
                                >
                                  <input
                                    type="text"
                                    value={option}
                                    onChange={(e) =>
                                      updateOption(
                                        section.id,
                                        field.id,
                                        optIdx,
                                        e.target.value,
                                      )
                                    }
                                    className="flex-1 text-xs border border-gray-200 rounded px-2 py-1"
                                  />
                                  <button
                                    onClick={() =>
                                      removeOption(section.id, field.id, optIdx)
                                    }
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    <XIcon className="h-3 w-3" />
                                  </button>
                                </div>
                              ))}
                              <button
                                onClick={() => addOption(section.id, field.id)}
                                className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center"
                              >
                                <PlusIcon className="h-3 w-3 mr-1" />
                                Add Option
                              </button>
                            </div>
                          )}
                          {/* Slider range */}
                          {field.type === 'slider' && (
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="text-xs text-gray-600">
                                  Min:
                                </label>
                                <input
                                  type="number"
                                  value={field.min}
                                  onChange={(e) =>
                                    updateField(section.id, field.id, {
                                      min: parseInt(e.target.value),
                                    })
                                  }
                                  className="w-full text-xs border border-gray-200 rounded px-2 py-1"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-gray-600">
                                  Max:
                                </label>
                                <input
                                  type="number"
                                  value={field.max}
                                  onChange={(e) =>
                                    updateField(section.id, field.id, {
                                      max: parseInt(e.target.value),
                                    })
                                  }
                                  className="w-full text-xs border border-gray-200 rounded px-2 py-1"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    {/* Add Field Dropdown */}
                    <div className="relative">
                      <select
                        onChange={(e) => {
                          if (e.target.value) {
                            addField(
                              section.id,
                              e.target.value as PreferenceFieldType,
                            )
                            e.target.value = ''
                          }
                        }}
                        className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white hover:bg-gray-50 cursor-pointer"
                      >
                        <option value="">+ Add Field</option>
                        {fieldTypeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
                {preferenceSections.length === 0 && (
                  <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
                    <SlidersIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 mb-4">No sections added yet</p>
                    <button
                      onClick={addSection}
                      className="text-indigo-600 hover:text-indigo-700 font-medium"
                    >
                      Add your first section
                    </button>
                  </div>
                )}
              </div>
              {/* Right: Live Preview */}
              <div className="lg:sticky lg:top-6 h-fit">
                <div className="flex items-center space-x-2 mb-4">
                  <EyeIcon className="h-5 w-5 text-gray-400" />
                  <h3 className="font-semibold text-gray-700">Live Preview</h3>
                </div>
                <PreferencePreview />
              </div>
            </div>
          </div>
          {/* Styling Options */}
          <div className="bg-white rounded-lg p-6 mb-8 shadow-sm">
            <h2 className="text-lg font-semibold mb-6 flex items-center">
              <SparklesIcon className="h-5 w-5 mr-2 text-indigo-600" />
              Form Styling
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Layout */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Layout
                </label>
                <div className="space-y-2">
                  <label className="flex items-center p-3 border-2 border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="layout"
                      checked={formStyling.layout === 'single'}
                      onChange={() =>
                        setFormStyling({ ...formStyling, layout: 'single' })
                      }
                      className="mr-3"
                    />
                    <div>
                      <span className="font-medium">Single Column</span>
                      <p className="text-xs text-gray-500">
                        Fields stack vertically
                      </p>
                    </div>
                  </label>
                  <label className="flex items-center p-3 border-2 border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="layout"
                      checked={formStyling.layout === 'two-column'}
                      onChange={() =>
                        setFormStyling({
                          ...formStyling,
                          layout: 'two-column',
                        })
                      }
                      className="mr-3"
                    />
                    <div>
                      <span className="font-medium">Two Columns</span>
                      <p className="text-xs text-gray-500">
                        Fields side by side
                      </p>
                    </div>
                  </label>
                </div>
              </div>
              {/* Color Scheme */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Color Scheme
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {['indigo', 'blue', 'purple', 'green', 'orange', 'gray'].map(
                    (color) => (
                      <button
                        key={color}
                        onClick={() =>
                          setFormStyling({
                            ...formStyling,
                            colorScheme: color as any,
                            buttonColor: color,
                          })
                        }
                        className={`p-3 rounded-lg border-2 transition-all ${formStyling.colorScheme === color ? 'border-gray-900 ring-2 ring-gray-900 ring-offset-2' : 'border-gray-200 hover:border-gray-300'}`}
                      >
                        <div
                          className={`h-8 w-full rounded bg-${color}-600`}
                        ></div>
                        <span className="text-xs mt-1 block capitalize">
                          {color}
                        </span>
                      </button>
                    ),
                  )}
                </div>
              </div>
              {/* Button Style */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Button Style
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() =>
                      setFormStyling({ ...formStyling, buttonStyle: 'square' })
                    }
                    className={`p-3 border-2 rounded-lg ${formStyling.buttonStyle === 'square' ? 'border-indigo-600 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <div className="h-8 bg-gray-300 rounded-none"></div>
                    <span className="text-xs mt-1 block">Square</span>
                  </button>
                  <button
                    onClick={() =>
                      setFormStyling({
                        ...formStyling,
                        buttonStyle: 'rounded',
                      })
                    }
                    className={`p-3 border-2 rounded-lg ${formStyling.buttonStyle === 'rounded' ? 'border-indigo-600 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <div className="h-8 bg-gray-300 rounded-lg"></div>
                    <span className="text-xs mt-1 block">Rounded</span>
                  </button>
                  <button
                    onClick={() =>
                      setFormStyling({ ...formStyling, buttonStyle: 'pill' })
                    }
                    className={`p-3 border-2 rounded-lg ${formStyling.buttonStyle === 'pill' ? 'border-indigo-600 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <div className="h-8 bg-gray-300 rounded-full"></div>
                    <span className="text-xs mt-1 block">Pill</span>
                  </button>
                </div>
              </div>
              {/* Border Radius */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Field Border Radius
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { value: 'none', label: 'None' },
                    { value: 'small', label: 'Small' },
                    { value: 'medium', label: 'Medium' },
                    { value: 'large', label: 'Large' },
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() =>
                        setFormStyling({
                          ...formStyling,
                          borderRadius: option.value as any,
                        })
                      }
                      className={`p-3 border-2 rounded-lg ${formStyling.borderRadius === option.value ? 'border-indigo-600 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'}`}
                    >
                      <span className="text-xs block">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              {/* Spacing */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Field Spacing
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'compact', label: 'Compact' },
                    { value: 'normal', label: 'Normal' },
                    { value: 'relaxed', label: 'Relaxed' },
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() =>
                        setFormStyling({
                          ...formStyling,
                          spacing: option.value as any,
                        })
                      }
                      className={`p-3 border-2 rounded-lg ${formStyling.spacing === option.value ? 'border-indigo-600 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'}`}
                    >
                      <span className="text-sm font-medium">
                        {option.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {/* Custom App Contact */}
      {appType === 'custom' && (
        <div className="bg-white rounded-lg p-6 mb-8 shadow-sm text-center">
          <h2 className="text-lg font-semibold mb-4">Contact Our Team</h2>
          <p className="text-gray-600 mb-6">
            Get in touch with our development team to receive your Developer
            Credentials and start building custom AI applications.
          </p>
          <button className="bg-indigo-600 text-white px-6 py-2 rounded-md hover:bg-indigo-700 inline-flex items-center">
            <SendIcon className="h-4 w-4 mr-2" />
            Contact Us
          </button>
        </div>
      )}
      {/* Main Form */}
      {(appType === 'prompt' ||
        (appType === 'template' && selectedTemplate !== '')) && (
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                App Name
                <InfoTooltip text="This will be the name of your app in the app store" />
              </label>
              <input
                type="text"
                className="w-full border border-gray-200 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Enter app name"
                value={formData.title}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    title: e.target.value,
                  })
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                Slug (App URL)
                <InfoTooltip text="This is how you will be able to link to your app, e.g. realuseai.com/app/<slug>" />
              </label>
              <input
                type="text"
                className="w-full border border-gray-200 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="my-app-name"
                value={formData.slug}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    slug: e.target.value,
                  })
                }
              />
              <p className="mt-1 text-xs text-gray-500">
                Use lowercase letters, numbers, and hyphens only
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                Description
                <InfoTooltip text="A brief description of what your app does. This will be visible to users browsing the app store" />
              </label>
              <textarea
                className="w-full border border-gray-200 rounded-md px-4 py-2 h-32 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Describe what your app does"
                value={formData.description}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    description: e.target.value,
                  })
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4 flex items-center">
                App Icon
                <InfoTooltip text="Choose an icon and color that represents your app" />
              </label>
              {/* Icon Preview */}
              <div className="flex items-center space-x-4 mb-4">
                <div
                  className={`w-20 h-20 rounded-2xl ${iconColor} flex items-center justify-center text-white shadow-lg`}
                >
                  {icons.find((i) => i.id === selectedIcon)?.icon}
                </div>
                <div>
                  <p className="text-sm text-gray-600">Preview</p>
                  <p className="text-xs text-gray-500">
                    This is how your icon will appear
                  </p>
                </div>
              </div>
              {/* Icon Selection */}
              <div className="mb-4">
                <label className="block text-sm text-gray-600 mb-2">
                  Select Icon
                </label>
                <button
                  onClick={() => setShowIconSelect(!showIconSelect)}
                  className="w-full flex items-center justify-between border border-gray-200 rounded-md px-4 py-3 bg-white hover:bg-gray-50"
                >
                  <div className="flex items-center">
                    {icons.find((i) => i.id === selectedIcon)?.icon}
                    <span className="ml-3">
                      {icons.find((i) => i.id === selectedIcon)?.name}
                    </span>
                  </div>
                  <ChevronDownIcon
                    className={`h-5 w-5 text-gray-400 transition-transform ${showIconSelect ? 'rotate-180' : ''}`}
                  />
                </button>
                {showIconSelect && (
                  <div className="mt-2 border border-gray-200 rounded-lg p-4 bg-white max-h-80 overflow-y-auto">
                    <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                      {icons.map((icon) => (
                        <button
                          key={icon.id}
                          onClick={() => {
                            setSelectedIcon(icon.id)
                            setShowIconSelect(false)
                          }}
                          className={`p-3 rounded-lg border-2 flex flex-col items-center justify-center hover:bg-gray-50 transition-colors ${selectedIcon === icon.id ? 'border-indigo-600 bg-indigo-50' : 'border-gray-200'}`}
                          title={icon.name}
                        >
                          {icon.icon}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {/* Color Selection */}
              <div>
                <label className="block text-sm text-gray-600 mb-2">
                  Select Color
                </label>
                <button
                  onClick={() => setShowColorPicker(!showColorPicker)}
                  className="w-full flex items-center justify-between border border-gray-200 rounded-md px-4 py-3 bg-white hover:bg-gray-50"
                >
                  <div className="flex items-center">
                    <div className={`w-6 h-6 rounded ${iconColor} mr-3`} />
                    <span>
                      {colorOptions.find((c) => c.gradient === iconColor)
                        ?.name || 'Custom'}
                    </span>
                  </div>
                  <ChevronDownIcon
                    className={`h-5 w-5 text-gray-400 transition-transform ${showColorPicker ? 'rotate-180' : ''}`}
                  />
                </button>
                {showColorPicker && (
                  <div className="mt-2 border border-gray-200 rounded-lg p-4 bg-white">
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                      {colorOptions.map((color) => (
                        <button
                          key={color.id}
                          onClick={() => {
                            setIconColor(color.gradient)
                            setShowColorPicker(false)
                          }}
                          className={`relative h-16 rounded-lg ${color.gradient} border-2 ${iconColor === color.gradient ? 'border-gray-900 ring-2 ring-gray-900 ring-offset-2' : 'border-transparent hover:border-gray-300'} transition-all`}
                          title={color.name}
                        >
                          <span className="sr-only">{color.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <button className="flex items-center text-sm text-indigo-600 hover:text-indigo-700 mt-3">
                <UploadIcon className="h-4 w-4 mr-2" />
                <span>Upload custom icon</span>
              </button>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                Categories
                <InfoTooltip text="Select categories that best describe your app. This helps users discover your app" />
              </label>
              <div className="border border-gray-200 rounded-md p-4">
                <div className="flex flex-wrap gap-2">
                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={() => {
                        setSelectedCategories(
                          selectedCategories.includes(category)
                            ? selectedCategories.filter((c) => c !== category)
                            : [...selectedCategories, category],
                        )
                      }}
                      className={`px-3 py-1 rounded-full text-sm ${selectedCategories.includes(category) ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                AI Model
                <InfoTooltip text="Choose which AI model will power your app. Different models have different capabilities and pricing" />
              </label>
              <select
                className="w-full border border-gray-200 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={formData.aiModel}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    aiModel: e.target.value,
                  })
                }
              >
                <option value="">Select AI Model</option>
                {aiModels.map((model) => (
                  <option key={model.value} value={model.value}>
                    {model.label}
                  </option>
                ))}
              </select>
            </div>
            {appType === 'prompt' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  AI Instructions
                  <InfoTooltip text="These instructions will inform how the AI in your app interacts with prompts, such as 'Generate images in the style of 90s cartoons, of whatever text is entered by the user.', or 'Act like a professional cook's assistant, and give guidance based on expertise as a classically trained chef. Provide answers in both metric and imperial.' Etc." />
                </label>
                <textarea
                  className="w-full border border-gray-200 rounded-md px-4 py-2 h-32 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter your AI instructions here"
                />
                <p className="mt-2 text-sm text-gray-500 flex items-center">
                  <AlertCircleIcon className="h-4 w-4 mr-1" />
                  Use variables like {'{input}'} for dynamic content
                </p>
              </div>
            )}
            {/* App Features Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center">
                App Features
                <InfoTooltip text="Enable additional features for your app" />
              </label>
              <div className="space-y-4 border border-gray-200 rounded-lg p-4">
                {/* Image Upload */}
                <label className="flex items-start cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={formData.allowImageUpload}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        allowImageUpload: e.target.checked,
                      })
                    }
                    className="mt-1 mr-3 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <div>
                    <span className="font-medium text-gray-900 group-hover:text-indigo-600 transition-colors">
                      Allow Image Upload
                    </span>
                    <p className="text-sm text-gray-500 mt-0.5">
                      Users can upload images to your app for processing
                    </p>
                  </div>
                </label>
                {/* File Upload */}
                <div className="border-t border-gray-200 pt-4">
                  <label className="flex items-start cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={formData.allowFileUpload}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          allowFileUpload: e.target.checked,
                        })
                      }
                      className="mt-1 mr-3 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <div className="flex-1">
                      <span className="font-medium text-gray-900 group-hover:text-indigo-600 transition-colors">
                        Allow File Upload
                      </span>
                      <p className="text-sm text-gray-500 mt-0.5">
                        Users can upload documents and other files
                      </p>
                      {formData.allowFileUpload && (
                        <div className="mt-3 space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Allowed File Types
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                              {fileTypeOptions.map((option) => (
                                <label
                                  key={option.value}
                                  className="flex items-center cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    checked={formData.fileTypes.includes(
                                      option.value,
                                    )}
                                    onChange={() =>
                                      toggleFileType(option.value)
                                    }
                                    className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                  />
                                  <span className="text-sm text-gray-700">
                                    {option.label}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Maximum File Size
                            </label>
                            <select
                              value={formData.maxFileSize}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  maxFileSize: e.target.value,
                                })
                              }
                              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                              {fileSizeOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}
                    </div>
                  </label>
                </div>
                {/* Voice Input */}
                <div className="border-t border-gray-200 pt-4">
                  <label className="flex items-start cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={formData.enableVoiceInput}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          enableVoiceInput: e.target.checked,
                        })
                      }
                      className="mt-1 mr-3 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <div>
                      <span className="font-medium text-gray-900 group-hover:text-indigo-600 transition-colors">
                        Enable Voice Input
                      </span>
                      <p className="text-sm text-gray-500 mt-0.5">
                        Allow users to speak their prompts using speech-to-text
                      </p>
                    </div>
                  </label>
                </div>
                {/* Code Execution */}
                <div className="border-t border-gray-200 pt-4">
                  <label className="flex items-start cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={formData.enableCodeExecution}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          enableCodeExecution: e.target.checked,
                        })
                      }
                      className="mt-1 mr-3 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <div>
                      <span className="font-medium text-gray-900 group-hover:text-indigo-600 transition-colors">
                        Enable Code Execution
                      </span>
                      <p className="text-sm text-gray-500 mt-0.5">
                        Allow the AI to write and execute code (for development
                        and coding apps)
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                Visibility
                <InfoTooltip text="Control who can access your app. Private apps are only visible to you" />
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="visibility"
                    className="mr-2"
                    defaultChecked
                    value="private"
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        visibility: e.target.value,
                      })
                    }
                  />
                  <span>Private - Only you can access</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="visibility"
                    className="mr-2"
                    value="public"
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        visibility: e.target.value,
                      })
                    }
                  />
                  <span>Public - Anyone can use</span>
                </label>
              </div>
            </div>
          </div>
          {/* Form Actions */}
          <div className="mt-8 flex justify-end space-x-4">
            <button className="px-4 py-2 border border-gray-200 rounded-md hover:bg-gray-50">
              Save as Draft
            </button>
            <button
              onClick={handleCreateApp}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              Continue to Store Listing
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
// Helper components for icons
const CheckSquareIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
)
const CircleIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <circle cx="12" cy="12" r="10" strokeWidth={2} />
  </svg>
)
const ToggleLeftIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <rect x="3" y="8" width="18" height="8" rx="4" strokeWidth={2} />
    <circle cx="8" cy="12" r="3" fill="currentColor" />
  </svg>
)
